import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import {
  CouponForEstimateOrderScheme,
  EstimateOrderResponseDtoV1ShippingsItemOrderItemsItem,
} from '@/apis/rest/generated/types'
import { Image } from '@/components/common/images/Image'
import { Typo } from '@/components/common/typography'
import { couponApplyToastMessage } from '@/components/domains/order2/coupon-apply-toast-message'
import { ModalOrderProductCouponSelect } from '@/components/domains/order2/modal-order-product-coupon-select'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { useModal } from '@/containers/hooks'
import { useSetEstimateOrder } from '@/containers/order/set-estimate-order.hook'
import { doSetOrderPageCouponApplyMessage } from '@/stores/reduxData'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { isDateBefore } from '@/utils/utilDate'
import { currencyText, numberWithCommas } from '@/utils/utilNumber'
import { getImageUrl } from '@/utils/utilURL'

const CouponApplyText = ({
  appliedCoupon,
  countOfAvailableProductCoupon,
}: {
  appliedCoupon: CouponForEstimateOrderScheme | undefined
  countOfAvailableProductCoupon: number
}) => {
  const { color } = useTheme()
  return (
    <>
      {appliedCoupon ? (
        <div>
          <Typo variant="Body3 Regular" as="span">
            상품 쿠폰 적용
          </Typo>{' '}
          <Typo variant="Body3 Bold" as="span" color={color.blue500}>
            {`-${currencyText(appliedCoupon.appliedDiscountAmount)}`}
          </Typo>
        </div>
      ) : (
        <Typo variant="Body3 Regular" as="span">
          {`상품 쿠폰(${numberWithCommas(countOfAvailableProductCoupon || 0)})`}
        </Typo>
      )}
    </>
  )
}

interface OrderProductCardProps {
  product: EstimateOrderResponseDtoV1ShippingsItemOrderItemsItem
  isLast?: boolean
}

export const OrderProductCard = ({ product, isLast }: OrderProductCardProps) => {
  const { color } = useTheme()
  return (
    <>
      <StyledProductCard key={product.productName} isLast={isLast}>
        {/* product image */}
        <div
          style={{
            width: '5rem',
            height: '5rem',
            backgroundColor: GlobalColorEnum.Gray70,
            borderRadius: '0.8rem',
            border: `1px solid ${GlobalColorEnum.Gray70}`,
            overflow: 'hidden',
          }}
        >
          <Image src={getImageUrl(product.productMainImagePath) as string} alt="product-thumbnail" />
        </div>
        <StyledProductCardBody>
          <StyledProductCardTitle>
            <StyledProductName>
              <Typo variant="Body3 Regular">{product.productName}</Typo>
              <Typo variant="Body4 Regular" color={color.gray400}>
                {product.productOptionName}
              </Typo>
            </StyledProductName>
            <div>
              {!!product.couponDiscountPrice && (
                <Typo variant="Caption1 Bold" color={color.red500}>
                  쿠폰적용중
                </Typo>
              )}
              <div>
                <Typo variant="Body3 Bold" as="span" color={color.gray900}>
                  {currencyText(product.amount)}
                </Typo>
                {product.originalAmount !== product.amount && (
                  <StyledOriginalPrice>
                    <Typo
                      style={{
                        textDecoration: 'line-through',
                      }}
                      variant="Body4 Regular"
                      as="span"
                      color={color.gray300}
                    >
                      {currencyText(product.originalAmount)}
                    </Typo>
                  </StyledOriginalPrice>
                )}{' '}
                <Typo variant="Body4 Regular" as="span" color={color.gray500}>
                  {`/ ${numberWithCommas(product.quantity)}개`}
                </Typo>
              </div>
            </div>
          </StyledProductCardTitle>
        </StyledProductCardBody>
      </StyledProductCard>
    </>
  )
}

const StyledProductCard = styled.div<{ isLast?: boolean }>`
  display: flex;
  gap: 1.2rem;
  padding: 1.6rem;
  ${({ isLast }) =>
    !isLast &&
    `
        border-bottom: 1px solid ${GlobalColorEnum.Gray70};
    `}
`

const StyledProductCardBody = styled.div`
  flex: 1;
`

const StyledProductCardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledProductName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StyledOriginalPrice = styled.span`
  margin-left: 0.4rem;
`
